import { useState } from "react";
import { Quotes, X, ThumbsUp, ThumbsDown } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * ReflectionCard — shows the AI-generated reflection for an entry.
 * Variant "inline" is used on Dashboard right after a new entry is created
 * (dismissible). Variant "detail" is used on the EntryDetail page (always shown).
 *
 * When entryId is provided, displays a 👍 / 👎 feedback row underneath the
 * reflection text. Feedback is one-shot per entry (clicking either marks the
 * reflection as rated and disables the controls). Used to measure whether the
 * AI reflection is actually landing for users; data feeds into prompt tuning.
 */
export default function ReflectionCard({
  reflection,
  variant = "detail",
  onDismiss,
  entryId,
  initialRating, // "up" | "down" | null
}) {
  const [rating, setRating] = useState(initialRating || null);
  const [submitting, setSubmitting] = useState(false);
  const [thanks, setThanks] = useState(false);

  if (!reflection) return null;

  const isInline = variant === "inline";
  const canRate = Boolean(entryId);

  const handleRate = async (value) => {
    if (!canRate || rating || submitting) return;
    setSubmitting(true);
    setRating(value);
    try {
      await api.post(`/journal/entries/${entryId}/reflection-feedback`, {
        rating: value,
      });
      setThanks(true);
      // Fade thank-you after 2.5s
      setTimeout(() => setThanks(false), 2500);
    } catch (_) {
      // Silent fail; this is a signal-collection feature, not critical UX.
      setRating(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={
        isInline
          ? "card p-6 sm:p-7 grain border-brand/30 bg-brand/[0.04] relative animate-fade-up"
          : "card p-6 sm:p-7 grain border-brand/20 bg-brand/[0.03] relative"
      }
      data-testid="reflection-card"
    >
      {isInline && onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="absolute top-3 right-3 text-ink-500 hover:text-ink-900 p-1 rounded-full hover:bg-bone-200"
          aria-label="Dismiss reflection"
          data-testid="reflection-dismiss-btn"
        >
          <X size={14} />
        </button>
      )}
      <div className="flex items-start gap-3">
        <Quotes
          size={22}
          weight="fill"
          className="text-brand shrink-0 mt-1"
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <p className="eyebrow mb-2">{isInline ? "A reflection from Hush" : "Hush reflects"}</p>
          <p
            className="font-serif text-lg sm:text-xl text-ink-900 leading-[1.55] italic"
            data-testid="reflection-text"
          >
            {reflection}
          </p>

          {canRate && (
            <div
              className="mt-5 pt-4 border-t border-brand/15 flex items-center gap-2 text-[12px]"
              data-testid="reflection-feedback"
            >
              {thanks ? (
                <span
                  className="text-ink-500 italic animate-fade-up"
                  data-testid="reflection-feedback-thanks"
                >
                  Thank you — that helps Hush listen better.
                </span>
              ) : rating ? (
                <span className="text-ink-500" data-testid="reflection-feedback-given">
                  You rated this reflection {rating === "up" ? "helpful" : "not quite"}.
                </span>
              ) : (
                <>
                  <span className="text-ink-500 mr-1">Did this reflection feel useful?</span>
                  <button
                    type="button"
                    onClick={() => handleRate("up")}
                    disabled={submitting}
                    className="p-2 rounded-full text-ink-500 hover:text-brand hover:bg-brand/10 transition-colors disabled:opacity-50"
                    aria-label="Helpful reflection"
                    data-testid="reflection-feedback-up-btn"
                  >
                    <ThumbsUp size={16} weight="regular" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRate("down")}
                    disabled={submitting}
                    className="p-2 rounded-full text-ink-500 hover:text-brand hover:bg-brand/10 transition-colors disabled:opacity-50"
                    aria-label="Not quite right"
                    data-testid="reflection-feedback-down-btn"
                  >
                    <ThumbsDown size={16} weight="regular" />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
